import { SERVICE_PROVIDER } from "@/components/data/constants";
import { getActiveSessionState } from "@/hooks/useActiveSession";
import axiosInstance from "@/lib/axiosInstance";
import { isEntityScoped } from "@/store/entityTenantStore";
import { useImpersonationStore } from "@/store/useImpersonationStore";
import type { CustomAxiosRequestConfig } from "@/types/common.types";
import { queryOptions } from "@tanstack/react-query";
import type { EffectiveAccess, EffectiveAccessResponse } from "./access.types";

export const ACCESS_QUERY_ROOT = "access";
export const SERVENGINE_LOGIN = "servengine";

const EFFECTIVE_ACCESS_ENDPOINT = "/access/effective";

export const accessKeys = {
  effective: (entityTenant: string) => [ACCESS_QUERY_ROOT, entityTenant] as const,
};

// The backend ignores `acting-entity-id` for these user types (auth/actingEntity.ts), so their
// login is always their own.
const ACTING_HEADER_IGNORED_FOR = new Set([SERVICE_PROVIDER, "service_account"]);

export interface LoginSession {
  isEntityLogin?: boolean;
  tenantId?: string | null;
  userType?: string | null;
  isImpersonating?: boolean;
}

export const getLoginSession = (): LoginSession => ({
  ...getActiveSessionState(),
  isImpersonating: useImpersonationStore.getState().isImpersonating,
});

/**
 * The entity the backend answers a request in, or null when it answers at the session's own
 * login. axiosInstance sends `acting-entity-id` only for an entity scope and never while
 * impersonating, and the backend drops it for a service provider or service account.
 */
export const actingTenantFor = (entityTenant: string, session: LoginSession): string | null =>
  isEntityScoped(entityTenant) &&
  !session.isImpersonating &&
  !ACTING_HEADER_IGNORED_FOR.has(session.userType ?? "")
    ? entityTenant
    : null;

/**
 * The backend answers `GET /access/effective` only for the request's own login and refuses any
 * other `tenant` (contract 8.7): the acting entity when there is one, else the session's company
 * for a company login, else ServEngine.
 */
export const loginValueFor = (entityTenant: string, session: LoginSession): string =>
  actingTenantFor(entityTenant, session) ??
  (session.isEntityLogin && session.tenantId ? session.tenantId : SERVENGINE_LOGIN);

export async function fetchEffectiveAccess(
  entityTenant: string,
  signal?: AbortSignal,
): Promise<EffectiveAccess> {
  const { data } = await axiosInstance.get<EffectiveAccessResponse>(EFFECTIVE_ACCESS_ENDPOINT, {
    params: { tenant: loginValueFor(entityTenant, getLoginSession()) },
    actingEntityId: entityTenant,
    // The route guard falls back to the module check when this read fails; an alert on every
    // navigation would only repeat what the fallback already handles.
    suppressErrorAlert: true,
    signal,
  } as CustomAxiosRequestConfig);
  return data.data;
}

export const effectiveAccessQueryOptions = (entityTenant: string) =>
  queryOptions({
    queryKey: accessKeys.effective(entityTenant),
    queryFn: ({ signal }) => fetchEffectiveAccess(entityTenant, signal),
    staleTime: Infinity,
  });
